/**
 * Wrapper component for AddUserRoleDialog to work with subject user management
 * This bridges the interface gap between the generic dialog and the data management config
 */

import React from 'react';
import { AddUserRoleDialog, AddUserRoleDialogConfig } from '@shared/components/user-role';
import { useInviteSubjectUser } from '../hooks/useSubjectUsers';
import { useUserExists } from '@shared/hooks/useUserExists';
import { SubjectUserRole } from '../types';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

interface AddSubjectUserDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: any; // Not used for invite flow
  onSuccess?: () => void;
  tenantId: string;
  domainId: string;
  subjectId: string;
}

export const AddSubjectUserDialog: React.FC<AddSubjectUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  tenantId,
  domainId,
  subjectId,
}) => {
  const inviteMutation = useInviteSubjectUser(tenantId, domainId, subjectId);

  const RoleSelector = () => {
    const {
      control,
      formState: { errors },
    } = useFormContext();

    return (
      <Controller
        name="role"
        control={control}
        rules={{ required: 'Role is required' }}
        defaultValue={SubjectUserRole.PROFESSOR}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.role}>
            <InputLabel>Role</InputLabel>
            <Select {...field} label="Role">
              <MenuItem value={SubjectUserRole.PROFESSOR}>Lecturer</MenuItem>
              <MenuItem value={SubjectUserRole.COURSE_REPRESENTATIVE}>
                Course Representative
              </MenuItem>
            </Select>
          </FormControl>
        )}
      />
    );
  };

  const config: AddUserRoleDialogConfig = {
    title: 'Add Subject User',
    description: 'Invite a user to participate in this subject.',
    submitLabel: 'Send Invitation',
    emailLabel: 'Email Address',
    nameLabel: 'Full Name',
    nameRequired: false,
    additionalFields: <RoleSelector />,
    onSubmit: async (data) => {
      await inviteMutation.mutateAsync({
        email: data.email,
        name: data.name || undefined,
        role: data.role as SubjectUserRole,
      });
    },
    useUserExistsHook: (email, enabled) => {
      const result = useUserExists(email, { enabled });
      return {
        data: result.data
          ? {
              exists: result.exists ?? false,
              user: result.exists
                ? {
                    name: result.data.user?.name,
                    email: result.data.user?.email || result.data.email,
                  }
                : undefined,
            }
          : undefined,
        isLoading: result.isLoading,
      };
    },
  };

  return (
    <AddUserRoleDialog
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      config={config}
      defaultValues={{ role: SubjectUserRole.PROFESSOR }}
      isLoading={inviteMutation.isPending}
    />
  );
};
